import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  PlusCircle,
  Pencil,
  Trash2,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  ExternalLink,
  GitMerge,
  GitPullRequest,
  Minus,
  Plus,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from './ui/dialog';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import ApiService from '@/services/api.service';
import BackgroundService from '@/services/bg.service';
import { toastService } from '@/services/toast.service';
import { cn } from '@/lib/utils';

// ─── Types ───────────────────────────────────────────────────

interface PublishChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Phase = 'planning' | 'review' | 'publishing' | 'pending_review';

/**
 * How publishing behaves for this tenant.
 *
 *   platform      copy Develop onto Production inside Pliable. What every tenant
 *                 without a git production branch has always done.
 *   git_merge     merge the Develop branch into the production branch, then
 *                 import it. Production is live when this returns.
 *   pull_request  open a pull request instead. NOTHING is live until someone
 *                 merges it, which is a different promise and has to be said
 *                 before the button is pressed, not after.
 */
type PublishMode = 'platform' | 'git_merge' | 'pull_request';

interface PublishModeInfo {
  mode: PublishMode;
  repo?: string;
  develop_branch?: string;
  prod_branch?: string;
  /**
   * Under review, Develop changes reach the git branch through a pull request.
   * Between a change and its merge the branch is BEHIND Pliable — and publishing
   * merges the branch, not Pliable. False here means publishing would ship what
   * was last reviewed rather than what is on screen.
   */
  develop_in_sync?: boolean;
  unsynced_changes?: number;
  sync_pull_request?: { number?: number; url?: string } | null;
  /** Changes are inside the debounce window — not pushed yet, nothing to merge. */
  sync_pending?: boolean;
}

interface PublishJobResult {
  status?: 'published' | 'pending_review';
  // True when the publish took over a production branch that had nothing on it
  // but template content — see PublishOutcome.ADOPTED.
  adopted_production_branch?: boolean;
  message?: string;
  pull_request?: { number?: number; url?: string; created?: boolean };
}

interface DiffItem {
  name?: string;
  label?: string;
  _id?: string;
  node_type?: string;
  [key: string]: unknown;
}

interface ModelDiff {
  created: DiffItem[];
  updated: DiffItem[];
  updated_diff: unknown[];
  deleted: DiffItem[];
  has_model_changes: boolean;
}

interface DashboardIssue {
  dashboard_id: string;
  dashboard_name: string;
  /** Null when the problem is the dashboard's own, e.g. a filter in its bar. */
  panel_title: string | null;
  problem: string;
}

interface DashboardIssues {
  count: number;
  dashboards: number;
  issues: DashboardIssue[];
}

interface PublishPlan {
  next_version_idx: number;
  diff: Record<string, ModelDiff>;
  dashboard_issues?: DashboardIssues;
  has_any_changes: boolean;
  total_created: number;
  total_updated: number;
  total_deleted: number;
}

type ChangeType = 'created' | 'updated' | 'deleted';

interface SelectedItem {
  modelType: string;
  changeType: ChangeType;
  index: number;
  item: DiffItem;
  diff?: unknown;
}

interface StructuredChange {
  attribute?: string;
  change_type?: string;
  new_value?: unknown;
  old_value?: unknown;
  original_value?: unknown;
  changes?: unknown;
  diff?: unknown;
}

// ─── Labels ──────────────────────────────────────────────────

const MODEL_TYPE_LABELS: Record<string, string> = {
  PipelineNodeRelationship: 'Relationship',
  Visualization: 'Visualization',
  Dashboard: 'Dashboard',
  Dimension: 'Dimension',
  Measure: 'Measure',
  Analysis: 'Analysis',
  DashboardSuperset: 'Dashboard Dataset',
};

const NODE_TYPE_LABELS: Record<string, string> = {
  SOURCE: 'Source',
  SOURCE_FILE: 'Source File',
  SEED: 'Seed',
  SEED_SOURCE: 'Seed Source',
  STAGING: 'Staging',
  BUSINESS_OBJECT: 'Business Object',
  CUSTOM: 'Custom',
  STACK: 'Stack',
  MERGE: 'Merge',
  DEDUPLICATE: 'Deduplicate',
  DIMENSION: 'Entity',
  SPLIT: 'Split',
  SUMMARIZE: 'Summarize',
  FACT: 'Fact',
};

const ATTRIBUTE_LABELS: Record<string, string> = {
  label: 'Label',
  description: 'Description',
  custom_sql: 'Custom SQL',
  last_modified_column: 'Last Modified Column',
  incremental_build: 'Incremental Build',
  incremental_timestamp_field_id: 'Incremental Timestamp Field',
  grouping_behavior: 'Grouping Behavior',
  node_type: 'Node Type',
  dedupe_rules: 'Dedup Rules',
  measure_ids: 'Measures',
  dimension_ids: 'Dimensions',
  special_node_args: 'Special Node Args',
  upstream_pre_filters: 'Upstream Pre-filters',
  composite_key: 'Composite Key',
  export_config: 'Export Config',
  fields: 'Columns',
  data_whitelist: 'Data Whitelist',
  is_calculation: 'Is Calculation',
  formula: 'Formula',
  column_name: 'Column Name',
  name: 'Name',
  pipeline_node_id: 'Pipeline Node',
  field_id: 'Field',
  aggregator: 'Aggregator',
  formatter: 'Formatter',
  parent_measure_ids: 'Parent Measures',
  dimension_join_trees: 'Dimension Connections',
  map_options: 'Source Column Mappings',
  type: 'Type',
  part_of_composite_key: 'Part of Composite Key',
  transformer: 'Transformer',
  merge_behavior: 'Merge Behavior',
  is_dimension: 'Is Dimension',
  translations: 'Translations',
};

// ─── Helpers ─────────────────────────────────────────────────

function getItemName(item: DiffItem): string {
  return item.name || item.label || item._id || 'Unnamed';
}

function getItemType(modelType: string, item: DiffItem): string {
  if (modelType === 'PipelineNode' && item.node_type) {
    return NODE_TYPE_LABELS[item.node_type] || item.node_type;
  }
  return MODEL_TYPE_LABELS[modelType] || modelType;
}

function formatScalar(value: unknown): string {
  if (value === null || value === undefined) return '(empty)';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'string') return value || '(empty)';
  return String(value);
}

function isScalar(value: unknown): boolean {
  return value === null || value === undefined || typeof value !== 'object';
}

function attrLabel(key: string): string {
  return ATTRIBUTE_LABELS[key] || key.replace(/_/g, ' ');
}

// ─── Change Type Badge ───────────────────────────────────────

function ChangeTypeBadge({ type }: { type: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'text-[10px] font-medium px-1.5 py-0',
        type === 'created' && 'border-green-600 text-green-600',
        type === 'deleted' && 'border-red-500 text-red-500',
        type === 'updated' && 'border-blue-600 text-blue-600',
        type === 'reorder' && 'border-yellow-600 text-yellow-600'
      )}
    >
      {type}
    </Badge>
  );
}

// ─── Scalar Diff (old → new) ─────────────────────────────────

function ScalarDiff({ oldVal, newVal }: { oldVal: unknown; newVal: unknown }) {
  return (
    <div className="flex items-center gap-2 flex-wrap text-sm">
      <code className="text-red-500/80 line-through bg-red-50 dark:bg-red-950/30 px-1.5 py-0.5 rounded text-xs">
        {formatScalar(oldVal)}
      </code>
      <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
      <code className="text-green-600 bg-green-50 dark:bg-green-950/30 px-1.5 py-0.5 rounded text-xs">
        {formatScalar(newVal)}
      </code>
    </div>
  );
}

// ─── Collapsible JSON Tree ───────────────────────────────────
// A generic tree view that renders any JSON value with expand/collapse.

function JsonTreeNode({
  label,
  value,
  defaultOpen = false,
  color,
}: {
  label?: string;
  value: unknown;
  defaultOpen?: boolean;
  color?: 'green' | 'red' | 'blue';
}) {
  const [open, setOpen] = useState(defaultOpen);

  const colorClass =
    color === 'green'
      ? 'text-green-600'
      : color === 'red'
        ? 'text-red-500/80'
        : color === 'blue'
          ? 'text-blue-600'
          : 'text-foreground';

  if (isScalar(value)) {
    return (
      <div className="flex items-start gap-1.5 py-0.5 text-xs font-mono">
        {label && <span className="text-muted-foreground shrink-0">{label}:</span>}
        <span className={colorClass}>{formatScalar(value)}</span>
      </div>
    );
  }

  const isArray = Array.isArray(value);
  const entries = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v] as const)
    : Object.entries(value as Record<string, unknown>);

  const bracket = isArray ? ['[', ']'] : ['{', '}'];

  return (
    <div className="text-xs font-mono">
      <button
        className="flex items-center gap-1 py-0.5 hover:bg-accent/50 rounded px-0.5 -ml-0.5"
        onClick={() => setOpen(!open)}
      >
        {open ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
        )}
        {label && <span className="text-muted-foreground">{label}:</span>}
        {!open && (
          <span className="text-muted-foreground">
            {bracket[0]} {entries.length} {isArray ? 'items' : 'keys'} {bracket[1]}
          </span>
        )}
      </button>
      {open && (
        <div className="ml-4 border-l border-border pl-2">
          {entries.map(([key, val]) => (
            <JsonTreeNode key={key} label={isArray ? `[${key}]` : key} value={val} color={color} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PipelineNode Field Diff ─────────────────────────────────
// Purpose-built renderer for field-level changes on PipelineNodes.

function FieldChangesView({
  changes,
}: {
  changes: Record<
    string,
    { new: Record<string, unknown>; original: Record<string, unknown>; diff: StructuredChange[] }
  >;
}) {
  return (
    <div className="space-y-2">
      {Object.entries(changes).map(([fieldId, field]) => {
        const fieldName = (field.new?.label as string) || (field.original?.label as string) || fieldId;
        const fieldType = (field.new?.type as string) || (field.original?.type as string);

        return (
          <div key={fieldId} className="border rounded-md overflow-hidden">
            <div className="bg-muted/50 px-3 py-2 flex items-center gap-2">
              <span className="font-medium text-sm">{fieldName}</span>
              {fieldType && (
                <Badge variant="outline" className="text-[10px] font-mono">
                  {fieldType}
                </Badge>
              )}
            </div>
            <div className="px-3 py-2 space-y-2">
              {field.diff.map((change, i) => {
                const label = attrLabel(change.attribute || '');
                const oldVal = change.old_value ?? change.original_value;
                const newVal = change.new_value;
                const changeType = change.change_type || 'updated';

                if (changeType === 'updated' && isScalar(oldVal) && isScalar(newVal)) {
                  return (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground text-xs w-32 shrink-0">{label}</span>
                      <ScalarDiff oldVal={oldVal} newVal={newVal} />
                    </div>
                  );
                }

                if (changeType === 'created' || changeType === 'deleted') {
                  return (
                    <div key={i}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-muted-foreground text-xs">{label}</span>
                        <ChangeTypeBadge type={changeType} />
                      </div>
                      <JsonTreeNode
                        value={newVal ?? oldVal}
                        defaultOpen={false}
                        color={changeType === 'created' ? 'green' : 'red'}
                      />
                    </div>
                  );
                }

                // Complex updated value
                return (
                  <div key={i}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-muted-foreground text-xs">{label}</span>
                      <ChangeTypeBadge type={changeType} />
                    </div>
                    {oldVal !== undefined && newVal !== undefined ? (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-red-50 dark:bg-red-950/20 rounded p-2">
                          <JsonTreeNode value={oldVal} defaultOpen color="red" />
                        </div>
                        <div className="bg-green-50 dark:bg-green-950/20 rounded p-2">
                          <JsonTreeNode value={newVal} defaultOpen color="green" />
                        </div>
                      </div>
                    ) : (
                      <JsonTreeNode value={newVal ?? oldVal} defaultOpen />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CreatedOrDeletedFieldsList({
  fields,
  type,
}: {
  fields: Array<Record<string, unknown>>;
  type: 'created' | 'deleted';
}) {
  const color = type === 'created' ? 'green' : 'red';
  const Icon = type === 'created' ? Plus : Minus;

  return (
    <div className="space-y-1">
      {fields.map((field, i) => (
        <div
          key={i}
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded text-sm',
            type === 'created' && 'bg-green-50 dark:bg-green-950/20',
            type === 'deleted' && 'bg-red-50 dark:bg-red-950/20'
          )}
        >
          <Icon className={cn('h-3 w-3 shrink-0', `text-${color}-600`)} />
          <span className={`text-${color}-700 dark:text-${color}-400 font-medium`}>
            {(field.label as string) || (field.name as string) || `Field ${i + 1}`}
          </span>
          {field.type ? (
            <Badge variant="outline" className="text-[10px] font-mono">
              {field.type as string}
            </Badge>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ─── PipelineNode Structured Diff ────────────────────────────

function PipelineNodeDiffView({ diff }: { diff: StructuredChange[] }) {
  return (
    <div className="space-y-4">
      {diff.map((change, i) => {
        const label = attrLabel(change.attribute || '');
        const changeType = change.change_type || 'updated';
        const oldVal = change.old_value ?? change.original_value;
        const newVal = change.new_value;

        // Fields - the most complex and important case
        if (change.attribute === 'fields') {
          return (
            <div key={i}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium text-sm">{label}</span>
                <ChangeTypeBadge type={changeType} />
              </div>
              {changeType === 'updated' && change.changes ? (
                <FieldChangesView
                  changes={
                    change.changes as Record<
                      string,
                      { new: Record<string, unknown>; original: Record<string, unknown>; diff: StructuredChange[] }
                    >
                  }
                />
              ) : null}
              {changeType === 'created' && Array.isArray(newVal) && (
                <CreatedOrDeletedFieldsList fields={newVal as Array<Record<string, unknown>>} type="created" />
              )}
              {changeType === 'deleted' && Array.isArray(oldVal) && (
                <CreatedOrDeletedFieldsList fields={oldVal as Array<Record<string, unknown>>} type="deleted" />
              )}
              {changeType === 'reorder' && (
                <p className="text-xs text-muted-foreground">Column order was changed.</p>
              )}
            </div>
          );
        }

        // Simple scalar attribute change
        if (changeType === 'updated' && isScalar(oldVal) && isScalar(newVal)) {
          return (
            <div key={i} className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground w-40 shrink-0">{label}</span>
              <ScalarDiff oldVal={oldVal} newVal={newVal} />
            </div>
          );
        }

        // Complex attribute (data_whitelist, etc)
        return (
          <div key={i}>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-medium text-sm">{label}</span>
              <ChangeTypeBadge type={changeType} />
            </div>
            {oldVal !== undefined && newVal !== undefined ? (
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-red-50 dark:bg-red-950/20 rounded p-2">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Before</div>
                  <JsonTreeNode value={oldVal} defaultOpen color="red" />
                </div>
                <div className="bg-green-50 dark:bg-green-950/20 rounded p-2">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">After</div>
                  <JsonTreeNode value={newVal} defaultOpen color="green" />
                </div>
              </div>
            ) : (
              <JsonTreeNode
                value={newVal ?? oldVal}
                defaultOpen
                color={changeType === 'created' ? 'green' : changeType === 'deleted' ? 'red' : undefined}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Measure Structured Diff ─────────────────────────────────

function MeasureDiffView({ diff }: { diff: StructuredChange[] }) {
  return (
    <div className="space-y-4">
      {diff.map((change, i) => {
        const label = attrLabel(change.attribute || '');
        const changeType = change.change_type || 'updated';
        const oldVal = change.old_value ?? change.original_value;
        const newVal = change.new_value;

        // Dimension join trees
        if (change.attribute === 'dimension_join_trees') {
          return (
            <div key={i}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-medium text-sm">{label}</span>
                <ChangeTypeBadge type={changeType} />
              </div>
              {changeType === 'updated' && Array.isArray(change.changes) && (
                <div className="space-y-2">
                  {(change.changes as Array<{ pipeline_node_id: string; original_value: unknown; new_value: unknown }>).map(
                    (jt, idx) => (
                      <div key={idx} className="grid grid-cols-2 gap-2">
                        <div className="bg-red-50 dark:bg-red-950/20 rounded p-2">
                          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Before</div>
                          <JsonTreeNode value={jt.original_value} defaultOpen color="red" />
                        </div>
                        <div className="bg-green-50 dark:bg-green-950/20 rounded p-2">
                          <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">After</div>
                          <JsonTreeNode value={jt.new_value} defaultOpen color="green" />
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
              {(changeType === 'created' || changeType === 'deleted') && (
                <JsonTreeNode
                  value={newVal ?? oldVal}
                  defaultOpen
                  color={changeType === 'created' ? 'green' : 'red'}
                />
              )}
            </div>
          );
        }

        // Simple scalar
        if (changeType === 'updated' && isScalar(oldVal) && isScalar(newVal)) {
          return (
            <div key={i} className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground w-40 shrink-0">{label}</span>
              <ScalarDiff oldVal={oldVal} newVal={newVal} />
            </div>
          );
        }

        // Complex
        return (
          <div key={i}>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-medium text-sm">{label}</span>
              <ChangeTypeBadge type={changeType} />
            </div>
            {oldVal !== undefined && newVal !== undefined ? (
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-red-50 dark:bg-red-950/20 rounded p-2">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Before</div>
                  <JsonTreeNode value={oldVal} defaultOpen color="red" />
                </div>
                <div className="bg-green-50 dark:bg-green-950/20 rounded p-2">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">After</div>
                  <JsonTreeNode value={newVal} defaultOpen color="green" />
                </div>
              </div>
            ) : (
              <JsonTreeNode
                value={newVal ?? oldVal}
                defaultOpen
                color={changeType === 'created' ? 'green' : changeType === 'deleted' ? 'red' : undefined}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Generic jsondiff View ───────────────────────────────────
// For model types that don't have structured diffs (Dimension, Dashboard, etc).
// jsondiff with marshal=True produces {field: newValue, $delete: {...}, $insert: {...}}

function JsonDiffTreeView({ diff }: { diff: Record<string, unknown> }) {
  const regularEntries = Object.entries(diff).filter(([k]) => !k.startsWith('$'));
  const deletions = diff['$delete'] as Record<string, unknown> | undefined;
  const insertions = diff['$insert'] as Record<string, unknown> | undefined;

  if (regularEntries.length === 0 && !deletions && !insertions) {
    return <p className="text-sm text-muted-foreground">No detailed changes available.</p>;
  }

  return (
    <div className="space-y-3">
      {regularEntries.map(([key, value]) => (
        <div key={key} className="flex items-start gap-3">
          <span className="text-sm text-muted-foreground w-40 shrink-0">{attrLabel(key)}</span>
          <div>
            <ChangeTypeBadge type="updated" />
            <div className="mt-1">
              {isScalar(value) ? (
                <code className="text-green-600 bg-green-50 dark:bg-green-950/30 px-1.5 py-0.5 rounded text-xs">
                  {formatScalar(value)}
                </code>
              ) : (
                <JsonTreeNode value={value} defaultOpen color="blue" />
              )}
            </div>
          </div>
        </div>
      ))}
      {insertions &&
        Object.entries(insertions).map(([key, value]) => (
          <div key={`ins-${key}`} className="flex items-start gap-3">
            <span className="text-sm text-muted-foreground w-40 shrink-0">{attrLabel(key)}</span>
            <div>
              <ChangeTypeBadge type="created" />
              <div className="mt-1">
                <JsonTreeNode value={value} defaultOpen color="green" />
              </div>
            </div>
          </div>
        ))}
      {deletions &&
        Object.entries(deletions).map(([key, value]) => (
          <div key={`del-${key}`} className="flex items-start gap-3">
            <span className="text-sm text-muted-foreground w-40 shrink-0">{attrLabel(key)}</span>
            <div>
              <ChangeTypeBadge type="deleted" />
              <div className="mt-1">
                <JsonTreeNode value={value} defaultOpen color="red" />
              </div>
            </div>
          </div>
        ))}
    </div>
  );
}

// ─── Diff Router ─────────────────────────────────────────────
// Picks the right renderer based on model type and diff shape.

function DiffDetailView({ modelType, diff }: { modelType: string; diff: unknown }) {
  if (!diff) {
    return <p className="text-sm text-muted-foreground">No detailed changes available.</p>;
  }

  // Structured diffs (PipelineNode, Measure) are arrays of change objects
  if (Array.isArray(diff)) {
    if (modelType === 'PipelineNode') {
      return <PipelineNodeDiffView diff={diff as StructuredChange[]} />;
    }
    if (modelType === 'Measure') {
      return <MeasureDiffView diff={diff as StructuredChange[]} />;
    }
    // Unknown structured format - render each entry through the tree
    return (
      <div className="space-y-3">
        {(diff as StructuredChange[]).map((change, i) => {
          const label = attrLabel(change.attribute || '');
          const changeType = change.change_type || 'updated';
          const oldVal = change.old_value ?? change.original_value;
          const newVal = change.new_value;

          if (changeType === 'updated' && isScalar(oldVal) && isScalar(newVal)) {
            return (
              <div key={i} className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground w-40 shrink-0">{label}</span>
                <ScalarDiff oldVal={oldVal} newVal={newVal} />
              </div>
            );
          }

          return (
            <div key={i}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm text-muted-foreground">{label}</span>
                <ChangeTypeBadge type={changeType} />
              </div>
              <JsonTreeNode value={newVal ?? oldVal} defaultOpen />
            </div>
          );
        })}
      </div>
    );
  }

  // jsondiff format (dict)
  if (typeof diff === 'object') {
    return <JsonDiffTreeView diff={diff as Record<string, unknown>} />;
  }

  return <p className="text-sm text-muted-foreground">No detailed changes available.</p>;
}

// ─── Detail Panel ────────────────────────────────────────────

function DetailPanel({ selected }: { selected: SelectedItem | null }) {
  if (!selected) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        Select an item to view details
      </div>
    );
  }

  const { item, changeType, modelType, diff } = selected;
  const name = getItemName(item);
  const type = getItemType(modelType, item);

  return (
    <div className="p-6 space-y-5">
      <div>
        <h3 className="text-lg font-semibold">{name}</h3>
        <div className="flex items-center gap-2 mt-1">
          <Badge variant="outline" className="text-xs">
            {type}
          </Badge>
          <Badge
            variant="outline"
            className={cn(
              'text-xs',
              changeType === 'created' && 'border-green-600 text-green-600',
              changeType === 'updated' && 'border-blue-600 text-blue-600',
              changeType === 'deleted' && 'border-destructive text-destructive'
            )}
          >
            {changeType}
          </Badge>
        </div>
      </div>

      {changeType === 'updated' && diff ? (
        <DiffDetailView modelType={modelType} diff={diff} />
      ) : null}

      {changeType === 'created' && (
        <p className="text-sm text-muted-foreground">
          This is a new object that will be published to production.
        </p>
      )}

      {changeType === 'deleted' && (
        <p className="text-sm text-muted-foreground">
          This object will be removed from production.
        </p>
      )}
    </div>
  );
}

// ─── Main Dialog ─────────────────────────────────────────────

export function PublishChangesDialog({ open, onOpenChange }: PublishChangesDialogProps) {
  const [phase, setPhase] = useState<Phase>('planning');
  const [plan, setPlan] = useState<PublishPlan | null>(null);
  const [publishMessage, setPublishMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SelectedItem | null>(null);
  const [publishMode, setPublishMode] = useState<PublishModeInfo>({ mode: 'platform' });
  const [pendingPr, setPendingPr] = useState<PublishJobResult['pull_request'] | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setPhase('planning');
      setPlan(null);
      setPublishMessage('');
      setError(null);
      setSelected(null);
      setPendingPr(null);
      fetchPublishMode();
      fetchPlan();
    }
  }, [open]);

  /**
   * Asked up front, not after. A publish that opens a pull request is a different
   * act from one that goes live, and the button has to say which before someone
   * commits to it. Failure is non-fatal — an older backend has no such endpoint,
   * and the in-platform copy is the correct assumption.
   */
  const fetchPublishMode = async () => {
    try {
      const resp = (await ApiService.getInstance().request(
        'GET',
        '/draftversions/publish-mode'
      )) as PublishModeInfo;
      setPublishMode(resp?.mode ? resp : { mode: 'platform' });
    } catch {
      setPublishMode({ mode: 'platform' });
    }
  };

  const fetchPlan = async () => {
    try {
      const resp = (await ApiService.getInstance().request('POST', '/draftversions/plan')) as {
        record: PublishPlan;
      };
      setPlan(resp.record);
      setPhase('review');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate publish plan';
      setError(message);
      toastService.error('Failed to generate publish plan', { description: message });
      onOpenChange(false);
    }
  };

  const handlePublish = async () => {
    if (!plan) return;

    setPhase('publishing');
    setError(null);

    try {
      const resp = (await ApiService.getInstance().request('POST', '/draftversions/publish', {
        next_version_idx: plan.next_version_idx,
        publish_message: publishMessage,
      })) as { job_id: string };

      const result = (await BackgroundService.getInstance().waitForJob(
        resp.job_id
      )) as PublishJobResult | null;

      // A pull request is NOT a publish. Leaving Develop mode and navigating away
      // here would tell the user their changes were live when they are sitting in
      // a pull request nobody has merged.
      if (result?.status === 'pending_review') {
        setPendingPr(result.pull_request ?? null);
        setPhase('pending_review');
        return;
      }

      localStorage.removeItem('draft_version_id');
      queryClient.setQueryData(['draft_version_id'], null);
      queryClient.invalidateQueries();

      // First publish for a tenant whose production branch had never been written:
      // it shared no history with Develop, so it was moved onto Develop's head
      // rather than merged into. Worth saying — someone looking at the repo
      // afterwards will find the branch rewritten, not merged.
      toastService.success(
        result?.adopted_production_branch
          ? 'Changes published. The production branch held only template content, so it now tracks Develop.'
          : 'Changes published successfully'
      );
      onOpenChange(false);
      navigate('/');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to publish changes';
      toastService.error('Publish failed', { description: message });
      setError(message);
      setPhase('review');
    }
  };

  const handleSelect = useCallback(
    (modelType: string, changeType: ChangeType, index: number, item: DiffItem, diff?: unknown) => {
      setSelected({ modelType, changeType, index, item, diff });
    },
    []
  );

  const isBlocked = phase === 'publishing';

  // Publishing is refused server-side in this state; disabling the button means
  // nobody has to discover that by pressing it.
  const blockedBySync = publishMode.develop_in_sync === false;

  const publishButtonLabel =
    publishMode.mode === 'pull_request' ? 'Open Publish Request' : 'Publish to Production';

  const publishModeNote = (() => {
    if (publishMode.mode === 'pull_request') {
      return (
        `This opens a pull request into ${publishMode.prod_branch ?? 'the production branch'} — ` +
        `it does not publish. Nothing reaches Production until someone merges it.`
      );
    }
    if (publishMode.mode === 'git_merge') {
      return (
        `Merges ${publishMode.develop_branch ?? 'Develop'} into ` +
        `${publishMode.prod_branch ?? 'the production branch'} and imports the result.`
      );
    }
    return null;
  })();

  const renderSidebarItems = (changeType: ChangeType, icon: React.ReactNode, label: string) => {
    if (!plan) return null;

    const items: { modelType: string; item: DiffItem; globalIndex: number; diff?: unknown }[] = [];
    for (const [modelType, modelDiff] of Object.entries(plan.diff)) {
      const list = modelDiff[changeType];
      for (let i = 0; i < list.length; i++) {
        items.push({
          modelType,
          item: list[i],
          globalIndex: items.length,
          diff: changeType === 'updated' ? modelDiff.updated_diff?.[i] : undefined,
        });
      }
    }

    if (items.length === 0) return null;

    return (
      <div className="space-y-0.5">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider px-2 pt-3 pb-1">
          {icon}
          {label} ({items.length})
        </div>
        {items.map((entry) => {
          const isSelected =
            selected?.changeType === changeType &&
            selected?.modelType === entry.modelType &&
            selected?.item === entry.item;

          return (
            <button
              key={`${changeType}-${entry.modelType}-${entry.globalIndex}`}
              className={cn(
                'w-full text-left px-3 py-2 text-sm rounded-md transition-colors',
                'hover:bg-accent',
                isSelected && 'bg-accent'
              )}
              onClick={() =>
                handleSelect(entry.modelType, changeType, entry.globalIndex, entry.item, entry.diff)
              }
            >
              <div className="font-medium truncate">{getItemName(entry.item)}</div>
              <div className="text-xs text-muted-foreground">
                {getItemType(entry.modelType, entry.item)}
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!isBlocked) onOpenChange(value);
      }}
    >
      <DialogContent
        className="!max-w-none !w-[calc(100vw-2rem)] !h-[calc(100vh-2rem)] !translate-x-[-50%] !translate-y-[-50%]"
        onEscapeKeyDown={(e) => {
          if (isBlocked) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (isBlocked) e.preventDefault();
        }}
        hideClose={isBlocked}
      >
        {(phase === 'planning' || phase === 'publishing') && (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {phase === 'planning'
                ? 'Analyzing your changes...'
                : publishMode.mode === 'pull_request'
                  ? 'Opening a publish request...'
                  : 'Publishing your changes to production...'}
            </p>
          </div>
        )}

        {/*
          A pull request was opened. Deliberately NOT treated as a publish: Develop
          mode stays on, nothing is invalidated, and the copy says plainly that
          Production is unchanged. Clearing the draft here — as the success path
          does — would tell someone their changes were live while they sat waiting
          for a review.
        */}
        {phase === 'pending_review' && (
          <div className="flex flex-col items-center justify-center h-full gap-4 px-8 text-center">
            <GitPullRequest className="h-10 w-10 text-muted-foreground" />
            <div className="space-y-2">
              <DialogTitle className="text-lg font-semibold">
                Publish request opened
              </DialogTitle>
              <p className="text-sm text-muted-foreground max-w-md">
                Your changes are proposed in a pull request into{' '}
                <span className="font-mono">{publishMode.prod_branch ?? 'production'}</span>.
                Production is unchanged until someone merges it — Pliable imports it
                automatically then.
              </p>
              <p className="text-xs text-muted-foreground">
                You are still in Develop mode, and can keep working.
              </p>
            </div>
            <div className="flex gap-2">
              {pendingPr?.url && (
                <Button asChild>
                  <a href={pendingPr.url} target="_blank" rel="noreferrer">
                    Review pull request
                    {pendingPr.number ? ` #${pendingPr.number}` : ''}
                    <ExternalLink className="ml-1 h-3 w-3" />
                  </a>
                </Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          </div>
        )}

        {phase === 'review' && plan && (
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b shrink-0">
              <DialogTitle className="text-lg font-semibold">Publish Changes</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground mt-1">
                Review your changes before publishing to production.
              </DialogDescription>
              <div className="flex gap-2 mt-3">
                {plan.total_created > 0 && (
                  <Badge variant="default" className="bg-green-600">
                    {plan.total_created} new
                  </Badge>
                )}
                {plan.total_updated > 0 && (
                  <Badge variant="default" className="bg-blue-600">
                    {plan.total_updated} updated
                  </Badge>
                )}
                {plan.total_deleted > 0 && (
                  <Badge variant="destructive">
                    {plan.total_deleted} deleted
                  </Badge>
                )}
                {!plan.has_any_changes && (
                  <span className="text-sm text-muted-foreground">No changes detected.</span>
                )}
              </div>

              {/*
                Broken dashboards, above everything else in the dialog.

                A panel pointing at a deleted metric still renders — empty, or
                erroring at read time — and nothing anywhere else says which
                change did it. Publish is the last moment the person who broke it
                is the person at the screen, so this is loud and sits before the
                diff rather than inside it.

                It does not block: a publish that refuses to run because a
                dashboard is wrong is worse than one that says so clearly.
              */}
              {!!plan.dashboard_issues?.count && (
                <Alert variant="destructive" className="mt-3">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>
                    {plan.dashboard_issues.count === 1
                      ? '1 dashboard problem'
                      : `${plan.dashboard_issues.count} dashboard problems`}
                    {plan.dashboard_issues.dashboards > 1
                      ? ` across ${plan.dashboard_issues.dashboards} dashboards`
                      : ''}
                  </AlertTitle>
                  <AlertDescription>
                    <p className="mb-2">
                      These panels reference things that no longer exist. Publishing
                      will not fix them — they will break in production.
                    </p>
                    <ul className="space-y-1 max-h-40 overflow-y-auto">
                      {plan.dashboard_issues.issues.map((issue, i) => (
                        <li key={`${issue.dashboard_id}-${i}`} className="text-xs">
                          <span className="font-medium">{issue.dashboard_name}</span>
                          {issue.panel_title ? ` › ${issue.panel_title}` : ''}
                          {' — '}
                          {issue.problem}
                        </li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/*
                What this button will actually do, stated before anyone reads the
                diff. It lived in the footer as muted text beside the buttons,
                which is the last place someone looks — and "this opens a pull
                request" is not a footnote, it is the difference between shipping
                and proposing.
              */}
              {publishModeNote && (
                <Alert className="mt-3">
                  {publishMode.mode === 'pull_request' ? (
                    <GitPullRequest className="h-4 w-4" />
                  ) : (
                    <GitMerge className="h-4 w-4" />
                  )}
                  <AlertDescription>{publishModeNote}</AlertDescription>
                </Alert>
              )}

              {/*
                The trap this closes: the diff below is Pliable's Develop vs
                Production, but publishing merges the git BRANCH into production.
                While a sync pull request is open those are different things, and
                nothing else on this screen would say so.
              */}
              {blockedBySync && (
                <Alert variant="destructive" className="mt-3">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="space-y-2">
                    {publishMode.sync_pending ? (
                      <p>
                        Recent changes haven&apos;t reached git yet — Pliable batches them for a
                        few seconds. Wait a moment, reopen this, and they&apos;ll be included.
                      </p>
                    ) : (
                      <p>
                        {publishMode.unsynced_changes ?? 'Some'} change
                        {publishMode.unsynced_changes === 1 ? ' is' : 's are'} still waiting in
                        Pliable&apos;s sync pull request, so{' '}
                        <span className="font-mono">{publishMode.develop_branch}</span> does not yet
                        have everything you see below. Publishing now would promote what was last
                        merged — not what you are looking at.
                      </p>
                    )}
                    {publishMode.sync_pull_request?.url && (
                      <Button asChild size="sm" variant="secondary">
                        <a
                          href={publishMode.sync_pull_request.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Review and merge
                          {publishMode.sync_pull_request.number
                            ? ` #${publishMode.sync_pull_request.number}`
                            : ''}
                          <ExternalLink className="ml-1 h-3 w-3" />
                        </a>
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Body: sidebar + detail */}
            <div className="flex flex-1 min-h-0">
              {/* Sidebar */}
              <div className="w-80 border-r flex flex-col shrink-0">
                <div className="flex-1 overflow-y-auto p-2">
                  {renderSidebarItems(
                    'created',
                    <PlusCircle className="h-3.5 w-3.5 text-green-600" />,
                    'New'
                  )}
                  {renderSidebarItems(
                    'updated',
                    <Pencil className="h-3.5 w-3.5 text-blue-600" />,
                    'Updated'
                  )}
                  {renderSidebarItems(
                    'deleted',
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />,
                    'Deleted'
                  )}
                </div>

                {/* Publish message in sidebar bottom */}
                <div className="border-t p-3 space-y-2 shrink-0">
                  <Label htmlFor="publish-message" className="text-xs">
                    {publishMode.mode === 'pull_request' ? 'Publish message & notes' : 'Publish message'}
                  </Label>
                  <Textarea
                    id="publish-message"
                    placeholder={
                      publishMode.mode === 'platform'
                        ? 'Describe your changes...'
                        : 'Short summary\n\nAnything reviewers should know — why, what to check, tickets.'
                    }
                    value={publishMessage}
                    onChange={(e) => setPublishMessage(e.target.value)}
                    rows={publishMode.mode === 'platform' ? 2 : 5}
                    className="text-sm"
                  />
                  {publishMode.mode !== 'platform' && (
                    <p className="text-xs text-muted-foreground">
                      {publishMode.mode === 'pull_request'
                        ? 'First line becomes the pull request title; everything after a blank line becomes its description.'
                        : 'First line becomes the merge commit subject; the rest becomes its body.'}
                    </p>
                  )}
                </div>
              </div>

              {/* Detail panel */}
              <div className="flex-1 min-w-0 overflow-y-auto">
                <DetailPanel selected={selected} />
              </div>
            </div>

            {/* Footer */}
            <div className="border-t px-6 py-3 flex items-center justify-end gap-2 shrink-0">
              {error && <p className="text-sm text-destructive mr-auto">{error}</p>}
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                onClick={handlePublish}
                disabled={!plan?.has_any_changes || blockedBySync}
              >
                {publishButtonLabel}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
